<template>
    <div class="mx-3">
        <file-upload-modal></file-upload-modal>
        <document-tags></document-tags>
        <confirmation-modal></confirmation-modal>

        <div class="alert alert-primary text-center" role="alert">
            <h5 class="font-weight-bold"><i class="fas fa-history"></i> HISTORICAL</h5>
            <div v-if="!documentExists">
                <i class="fas fa-exclamation-triangle fa-lg"></i>
                <span class="font-weight-bold ml-1">WARNING:</span>The orginally submitted CMS form document has not been uploaded.
            </div>            
        </div>

        <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
            <div v-if="documentExists">
                <input v-if="edit['description']" class="form-control form-control-lg w-75 mr-3" v-model="serviceForm.description" @blur="change('description')" @keyup.enter="change('description')" v-focus>
                <span v-else class="h2" @click="edit['description'] = true;" :title="serviceForm.description">                                
                    {{ serviceForm.description }}
                    <small><i class="fas fa-pencil-alt fa-xs text-primary"></i></small>
                </span>
            </div>
            <div v-else>&nbsp;</div>
            <div class="btn-toolbar mb-1" role="toolbar">
                <div class="btn-group mr-1"> 
                    <button v-if="documentExists" class="btn btn-primary" data-toggle="tooltip" title="Replace CMS Document" @click="confirmReplace()">
                        <i class="fas fa-exchange-alt fa-lg fa-fw"></i>
                    </button>                                
                    <a v-if="documentExists" :href="documentHref" target="_blank" class="btn btn-primary" data-toggle="tooltip" title="View CMS Document">
                        <i class="fas fa-file-pdf fa-lg fa-fw"></i>
                    </a>                    
                    <button class="btn btn-primary" data-toggle="tooltip" title="Document Tags" @click="onDocumentTags()">
                        <i class="fas fa-tags fa-lg fa-fw"></i>
                    </button>
                    <a class="btn btn-primary" :href="printHref" target="_blank" data-toggle="tooltip" title="Print Details">
                        <i class="fas fa-print fa-lg fa-fw"></i>
                    </a>                                  
                    <button v-if="!documentExists" type="button" class="btn btn-primary" title="Upload Original CMS Document" @click="uploadDocument(null, serviceForm.submit_date)">
                        <i class="fas fa-file-upload fa-lg fa-fw"></i>
                    </button>                    
                </div>
                <div class="btn-group">
                    <a :href="claimHref" class="btn btn-primary" data-toggle="tooltip" title="View Claim">
                        <i class="fas fa-folder fa-lg fa-fw"></i>
                    </a>                    
                </div>                                
            </div>
        </div>

        <div class="d-flex justify-content-end">
            <div v-for="tag in serviceForm.document_tags" :key="'historical-service-document-tag-' + tag.tag" class="badge border mt-2 mr-2">
                <button type="button" class="close" data-dismiss="alert" aria-label="Close" @click="confirmRemoveTag(tag)">
                    <span aria-hidden="true">&times;</span>
                </button>
                <span class="text-capitalize" v-tooltip:top="{creator: tag.creator, created_at: localize(tag.created_at)}">{{ tag.tag }}</span>             
            </div>            
        </div>

        <div class="container-fluid">
            <div class="row">
                <div class="col">
                    <span class="font-large">{{ serviceForm.patient_name }}</span>
                    <h5>Date of Birth: {{ serviceForm.patient_dob }}</h5>
                </div>
                <div class="col">
                    <div class="d-flex">
                        <div class="flex-column mr-2">
                            <h5>Submitted On:</h5>
                        </div>
                        <div class="flex-column">
                            <input v-if="edit['submit_date']" type="date" class="form-control form-control-lg" id="submitDate" v-model="serviceForm.submit_date" @blur="change('submit_date')" @keyup.enter="change('submit_date')" v-focus>
                            <span v-else class="h5" @click="edit['submit_date'] = true;">
                                {{ serviceForm.submit_date }}
                                <i class="fas fa-pencil-alt fa-xs text-primary"></i>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="p-3">
            <div class="container-fluid mb-3">
                <div class="row">
                    <div class="col">
                        <h4>Patient Account Number: {{ serviceForm.pan }}</h4>
                        <h4>Date of Service: {{ serviceForm.dos }}</h4>
                        <h5>Payer: {{ serviceForm.insurance_carrier }}</h5>
                        <h5>Physician: {{ serviceForm.physician }}</h5> 
                    </div>
                    <div class="col align-self-end">
                        <h5 v-if="serviceForm.past_due" class="col-sm-6 h5 text-danger">Days Past Due: {{ serviceForm.days_past_due }}</h5>
                        <h5 v-else>&nbsp;</h5>
                        <h5 :class="{'text-danger': serviceForm.balance > 0 }">Balance: {{ serviceForm.balance | currency }}</h5>
                    </div>
                </div>
            </div>
            <service-lines :client-id="serviceForm.client_id" :claim-id="serviceForm.claim_id" :service-form-id="serviceForm.id" :document-type-id="serviceForm.document_type_id" :lines="serviceForm.service_lines" :contract="serviceForm.contract" :allow-contracts="allowContracts" :historical="true"></service-lines>
            <service-payments :client-id="serviceForm.client_id" :claim-id="serviceForm.claim_id" :service-form-id="serviceForm.id" :payments="serviceForm.payments" :user-applied-payments="allowPayments" :historical="true"></service-payments>            
        </div>

        <service-associated-documents v-if="serviceForm.historical_document_id" :client-id="serviceForm.client_id" :claim-id="serviceForm.claim_id" :document-id="serviceForm.historical_document_id" :can-associate="canAssociate" :historical="true"></service-associated-documents>
        <br />
        <document-notes :data-client-id="serviceForm.client_id" :data-claim-id="serviceForm.claim_id" :data-document-id="serviceForm.id" :data-document-type-id="serviceForm.document_type_id" :data-historical="true"></document-notes>
    </div>    
</template>

<script>
import { DocumentType } from '../../constants.js';
import DocumentTags from '../documents/DocumentTagsComponent.vue';
import ServiceLines from '../services/ServiceLinesComponent.vue';
import ServicePayments from '../services/ServicePaymentsComponent.vue';

export default {
    components: {
        DocumentTags,
        ServiceLines,
        ServicePayments
    },
    props: {
        dataServiceForm: Object,
        options: Array,
        claimHref: String,
        allowContracts: Boolean,
        allowPayments: Boolean,
        canAssociate: Boolean
    },
    data() {
        return {
            serviceForm: this.dataServiceForm,
            description: this.dataServiceForm.description,
            edit: { description: false, submit_date: false },

            DocumentType: DocumentType
        }
    },
    computed: {
        documentExists: function () {
            return this.serviceForm.historical_document_id;
        },
        documentHref: function () {
            return `/clients/${this.serviceForm.client_id}/claims/${this.serviceForm.claim_id}/historical/documents/${this.serviceForm.historical_document_id}`;
        },
        printHref: function () {
            return `/clients/${this.serviceForm.client_id}/claims/${this.serviceForm.claim_id}/historical/services/${this.serviceForm.id}/print`;
        }
    },
    methods: {
        change: function (key) {

            this.edit[key] = false;

            if (this.serviceForm.historical_document_id) {

                axios.patch(`/ajax/clients/${this.serviceForm.client_id}/claims/${this.serviceForm.claim_id}/historical/services/${this.serviceForm.id}/documents/${this.serviceForm.historical_document_id}`, {
                    key: key,
                    value: this.serviceForm[key]
                });
            }
        },        
        uploadDocument: function (description, submitDate) {

            // default the selected option to first option in the list
            let selectedOption = this.options[0];

            // show the upload file modal
            eventHub.$emit('show-file-upload', {
                claimId: this.serviceForm.claim_id,
                url: `/ajax/clients/${this.serviceForm.client_id}/claims/${this.serviceForm.claim_id}/historical/services/${this.serviceForm.id}/document`,
                options: this.options,
                documentType: selectedOption.id,
                description: description,
                submitDate: submitDate
            });
        },
        onDocumentTags: function () {
            eventHub.$emit('show-document-tags', this.serviceForm.client_id, this.serviceForm.claim_id, this.serviceForm.id, this.serviceForm.document_type_id, true);
        }, 
        confirmRemoveTag: function (tag) {
            const title = "Remove Tag";
            const message = `<div>Are you sure you want to remove the ${tag.tag} tag?</div>`;
            eventHub.$emit('show-confirmation', title, message, this.removeTag, tag.id); 
        },
        removeTag: function (confirmed, id) {

            if (confirmed) {             
                axios.delete(`/ajax/clients/${this.serviceForm.client_id}/claims/${this.serviceForm.claim_id}/historical/services/${this.serviceForm.id}/tags/${id}`)
                    .then(response => {})
                    .catch(error => { console.log(error.response); });
            }
        },
        confirmReplace: function () {
            const title = "Replace CMS Document";
            const message = `<div>Are you sure you want to replace the existing CMS document?</div>`;
            eventHub.$emit('show-confirmation', title, message, this.replaceDocument, null);
        },
        replaceDocument: function (confirmed) {

            if (confirmed) {
                this.uploadDocument(this.serviceForm.description, this.serviceForm.submit_date);
            }
        },
        localize: function (value) {
            return this.$options.filters.localizeTime(value, 'YYYY-MM-DD HH:mm');
        }                                
    },
    created: function () {

        window.Echo.private('historical-service-form-event.' + this.serviceForm.id)
            .listen('ServiceFormEvent', (e) => {
                this.serviceForm = e.service;
            }); 
            
        window.Echo.private(`historical-document-tags-event.service.${this.serviceForm.id}.${this.serviceForm.client_id}`)
            .listen('DocumentTagsEvent', (e) => {
                this.$set(this.serviceForm, 'document_tags', e.tags);
            });            
    },
    directives: {
        focus: {
            inserted: function (el) {
                el.focus();
            }
        },
        tooltip: function (el, binding) {

            if (binding.value.creator) {

                if ($(el)) { $(el).popover('dispose'); }

                $(el).popover({
                    content:  '<small>Created by: ' + binding.value.creator + '<br/>' + 'Created at: ' + binding.value.created_at + '</small>',
                    placement: binding.arg,
                    trigger: 'hover',
                    html: true
                });
            }
        }        
    }        
}
</script>

<style scoped>
    .badge {
        padding: 10px;
        font-size: .9em;
    }

    .badge .close {
        margin-left: .25rem;
        color: inherit;
        font-size: 100%;
        text-shadow: 0 1px 0 rgba(#000, .5);
    }

    .font-large {
        font-size: 1.75em;
        font-weight: bold;
    }    
</style>